import { FocusTrapFactory } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import type { ModalSize } from '@mercadoni/stark-ui';
import { SkButtonComponent, SkDialogComponent } from '@mercadoni/stark-ui';

import type { AppError } from '../../../core/interfaces';
import { DialogStackService } from '../../../core/services/ui/dialog-stack.service';
import { translated } from '../../utils/translated.util';
import { NfErrorAlertComponent } from '../nf-error-alert/nf-error-alert.component';

/** Layers for the first, second and third stacked dialog; stark's own dialog is `z-50`. */
const Z_TIERS = [50, 60, 65] as const;

const FIELD_SELECTOR = 'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])';

let nextId = 0;

/**
 * Shell around stark-ui's `sk-dialog` that gives every Fury dialog the same behaviour: one close
 * rule for the X button, Escape, the backdrop and Cancelar (ignored while busy, discard prompt
 * when dirty), focus on open / trapped inside the card / returned to the opener, page scroll lock, a Spanish close
 * label, an accessible name even without a header, an inline error alert (`nf-error-alert`), stacking (only the
 * topmost dialog is interactive) and a standard footer slot.
 *
 * Mount it with `@if` like `sk-dialog`. Put the body as content and the footer buttons with the
 * `nf-dialog-actions` attribute (Cancelar first, the primary action last; a `mr-auto` class
 * pushes a tertiary action to the left). The host owns `busy` and `dirty` and must disable its
 * own buttons while busy.
 *
 * It is deliberately thin: delete it when stark-ui's dialog handles Escape, focus and stacking.
 */
@Component({
  selector: 'nf-dialog',
  imports: [NfErrorAlertComponent, SkDialogComponent, SkButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'contents',
    '(document:keydown.escape)': 'onEscape($event)',
  },
  template: `
    <sk-dialog
      [config]="config()"
      [closeOnBackdrop]="true"
      [closeAriaLabel]="closeLabel()"
      [attr.inert]="covered() ? '' : null"
      (closeRequested)="requestClose()">
      @if (error(); as e) {
        <nf-error-alert #alertEl class="mb-4 block" [error]="e" />
      }
      <ng-content />
      <ng-container ngProjectAs="[dialog-footer]">
        <ng-content select="[nf-dialog-actions]" />
      </ng-container>
    </sk-dialog>

    @if (discardOpen()) {
      <nf-dialog [title]="discardTitle()" size="sm" (closed)="discardOpen.set(false)">
        <p class="text-sm text-sk-text">{{ discardMessage() }}</p>
        <sk-button nf-dialog-actions data-nf-initial variant="outline" size="sm" (skClick)="discardOpen.set(false)">
          {{ keepEditingLabel() }}
        </sk-button>
        <sk-button nf-dialog-actions variant="danger" size="sm" (skClick)="discard()">{{ discardLabel() }}</sk-button>
      </nf-dialog>
    }
  `,
})
export class NfDialogComponent {
  private readonly doc = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly stack = inject(DialogStackService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly focusTrapFactory = inject(FocusTrapFactory);

  /** Dialog title; also its accessible name when the header is hidden. */
  readonly title = input.required<string>();
  /** Width of the card: sm 448px, md 672px, lg 896px, xl 1152px, full 95% of the viewport. */
  readonly size = input<ModalSize>('md');
  /** Sighted and screen-reader description of the dialog, when the title is not enough. */
  readonly description = input<string>();
  /** Whether the visible header (title and X) is shown. */
  readonly showHeader = input<boolean>(true);
  /** Whether the footer bar is rendered; turn it off for dialogs with no footer actions (for example a launcher). */
  readonly showFooter = input<boolean>(true);
  /** An action is in progress: every close path is ignored and the X is disabled. */
  readonly busy = input<boolean>(false);
  /** The form has unsaved changes: closing asks for confirmation first. */
  readonly dirty = input<boolean>(false);
  /** Failure of the last action, shown inline above the body. */
  readonly error = input<AppError | null>(null);

  /** Emitted when the dialog may close: after the discard prompt is accepted, or when nothing blocks it. */
  readonly closed = output<void>();

  protected readonly discardOpen = signal(false);
  protected readonly closeLabel = translated('common.dialog.close');
  protected readonly discardTitle = translated('common.dialog.discardTitle');
  protected readonly discardMessage = translated('common.dialog.discardMessage');
  protected readonly keepEditingLabel = translated('common.dialog.keepEditing');
  protected readonly discardLabel = translated('common.dialog.discard');

  private readonly id = `nf-dialog-${++nextId}`;
  private readonly depth = this.stack.push(this.id);
  private readonly opener = this.doc.activeElement instanceof HTMLElement ? this.doc.activeElement : null;
  private readonly alertEl = viewChild('alertEl', { read: ElementRef<HTMLElement> });

  protected readonly covered = computed(() => this.stack.isCovered(this.id));

  protected readonly config = computed(() => ({
    title: this.title(),
    description: this.description(),
    size: this.size(),
    showHeader: this.showHeader(),
    showFooter: this.showFooter(),
    showCloseButton: true,
  }));

  constructor() {
    afterNextRender(() => {
      this.stackAbove();
      this.syncPanel(this.busy(), this.showHeader(), this.title());
      this.trapFocus();
      this.focusInitial();
    });

    effect(() => this.syncPanel(this.busy(), this.showHeader(), this.title()));

    // Runs again when the alert element appears, so a failure while the body is scrolled stays visible.
    effect(() => {
      this.error();
      this.alertEl()?.nativeElement.scrollIntoView?.({ block: 'nearest' });
    });

    this.destroyRef.onDestroy(() => {
      this.stack.remove(this.id);
      if (this.opener?.isConnected) this.opener.focus();
    });
  }

  /**
   * The single close path for the X button, Escape, the backdrop and Cancelar: ignored while busy,
   * opens the discard prompt when the form is dirty, otherwise emits {@link closed}.
   */
  requestClose(): void {
    if (this.busy() || this.discardOpen()) return;
    if (this.dirty()) {
      this.discardOpen.set(true);
      return;
    }
    this.closed.emit();
  }

  protected discard(): void {
    this.discardOpen.set(false);
    this.closed.emit();
  }

  protected onEscape(event: Event): void {
    if (!this.stack.isTop(this.id) || event.defaultPrevented) return;
    // An open select, menu or popover inside the dialog takes Escape first.
    if (this.doc.activeElement?.closest('[aria-expanded="true"], [role="listbox"], [role="menu"]')) return;
    event.preventDefault();
    this.requestClose();
  }


  /** This dialog's card element, not one of a dialog nested in this host. */
  private panel(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>(':scope > sk-dialog [role="dialog"]');
  }

  private stackAbove(): void {
    const wrapper = this.panel()?.parentElement;
    if (wrapper && this.depth > 0) wrapper.style.zIndex = String(Z_TIERS[Math.min(this.depth, Z_TIERS.length - 1)]);
  }

  private syncPanel(busy: boolean, showHeader: boolean, title: string): void {
    const panel = this.panel();
    if (!panel) return;
    panel.setAttribute('aria-busy', String(busy));
    if (showHeader) panel.removeAttribute('aria-label');
    else panel.setAttribute('aria-label', title);
    const close = panel.querySelector<HTMLButtonElement>('[data-testid="sk-dialog-close"]');
    if (close) close.disabled = busy;
  }

  /** Keeps Tab and Shift+Tab inside this dialog's card; a nested dialog has its own trap. */
  private trapFocus(): void {
    const panel = this.panel();
    if (!panel) return;
    const trap = this.focusTrapFactory.create(panel);
    this.destroyRef.onDestroy(() => trap.destroy());
  }

  /** Moves focus into the card: the first invalid field, the marked control, the first field, or the card itself. */
  private focusInitial(): void {
    const panel = this.panel();
    if (!panel) return;
    const marked = panel.querySelector<HTMLElement>('[data-nf-initial]');
    const markedTarget = marked?.matches('button, input, textarea, select')
      ? marked
      : marked?.querySelector<HTMLElement>('button, input, textarea, select');
    const target =
      panel.querySelector<HTMLElement>('[aria-invalid="true"]') ??
      markedTarget ??
      panel.querySelector<HTMLElement>(FIELD_SELECTOR);
    if (target) {
      target.focus();
      return;
    }
    panel.tabIndex = -1;
    panel.focus();
  }
}
